import React, { useState, useEffect, useCallback } from "react";
import {
  Typography, Box, Paper, TextField, Button, Grid,
  FormControl, Alert, Autocomplete, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions, 
  CircularProgress, Tabs, Tab, Switch, FormControlLabel,
  InputAdornment, MenuItem, Select, InputLabel, Divider
} from "@mui/material";
import { product } from "../../api/paquetes";
import { useNotification } from "../../context/notification.context";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

// Constantes para destinos (reutilizadas de paquetes individuales)
const OPCIONES_DESTINO = [
  "San Juan", "Buenos Aires", "CABA", "Córdoba", "Mendoza", "Santa Fe", "Tucumán", "Entre Ríos", "Salta", "Misiones", "Chaco", "Santiago del Estero", "Corrientes", "La Pampa", "Jujuy", "Río Negro", "Neuquén", "Catamarca", "San Luis", "La Rioja", 
  "Santiago de Chile", "Bogotá", "Lima", "Ciudad de México", "Montevideo", "Brasilia",
  "Nueva York", "Londres", "París", "Tokio", "Dubái", "Los Ángeles", "Roma", "Madrid", "Seúl", "Ámsterdam", "Miami", "Barcelona", "Sao Paulo", "Toronto", "Rio de Janeiro"
];

const REQUISITOS_OPCIONES = [
  "Datos del pagador", "Colegio", "e-mail", "Bolso", "Dirección", "Teléfono", "Celular 1",
  "Celular 2", "Firmó", "Pagó", "Fecha de firma", "Foto Visa", "Monto de Pago", "Emisión",
  "Permiso", "Partida de nacimiento", "Vacuna", "DNI Físico", "Fotocopia de DNI", "Talle", "Ficha Médica",
  "VISA", "Pasaporte europeo", "Pasaporte", "CUIL"
];

// Interfaces para tipado de servicios (reutilizadas de paquetes individuales)
type BaseCompra = {
  proveedor: string;
  monedaCosto: string;
  precioCosto: number;
  nroFile?: string;
};

type ServicioAereo = {
  id: string;
  aerolinea: string;
  destino: string;
  descripcion: string;
  moneda: string;
  precioVenta: number;
  compra: BaseCompra;
};

type ServicioAlojamiento = {
  id: string;
  hotel: string;
  destino: string;
  noches: number;
  serviciosIncluidos: string;
  moneda: string;
  precioVenta: number;
  compra: BaseCompra;
};

type ServicioExtra = {
  id: string;
  servicio: string;
  moneda: string;
  precioVenta: number;
  compra: BaseCompra;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 4 }}>{children}</Box>}
  </div>
);

export const NuevoPresupuestoGrupal: React.FC<{ category?: string, onPackageCreated?: () => void }> = ({ category, onPackageCreated }) => {
  const [pestañaActiva, setPestañaActiva] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  
  // Estado principal del formulario con los nuevos campos
  const [datosFormulario, setDatosFormulario] = useState({
    concepto: "", 
    destino: "", 
    destinoNombre: "",
    fechaSalida: null as Dayjs | null,
    viajaAlExterior: false,
    cantidadColectivos: 1,
    monedaPrincipal: "USD",
    totalVentaManual: 0,
    totalCostoManual: 0,
    adultos: 0, 
    ninos: 0, 
    infantes: 0, 
    descripcion: "",
    cliente: "", 
    telefonoCliente: "", 
    emailCliente: "",
    anioViaje: new Date().getFullYear().toString(),
    // Nuevos campos simplificados
    montoServicio: 0,
    montoSuplementario: 0,
    percepcion: 0,
    cantidadPasajeros: 0,
    // Campos específicos para eventos
    nombreEvento: "",
    anioEvento: new Date().getFullYear().toString(),
    tipoEvento: "",
    cobroAsistentes: false,
    requisitos: [] as string[],
  });
  
  const [errores, setErrores] = useState<{[key: string]: string}>({});
  
  const manejarCambioInput = (campo: string, valor: any) => {
    setDatosFormulario(prev => {
      const nuevoEstado = { ...prev, [campo]: valor };
      // Si cambia la fecha de salida, actualizamos automáticamente el año del viaje
      if (campo === 'fechaSalida' && valor) {
        nuevoEstado.anioViaje = valor.year().toString();
      }
      return nuevoEstado;
    });
    if (errores[campo]) setErrores(prev => ({ ...prev, [campo]: "" }));
  };

  const toggleRequisito = (requisito: string) => {
    setDatosFormulario((prev) => {
      const existe = prev.requisitos.includes(requisito);
      const nuevos = existe
        ? prev.requisitos.filter((r) => r !== requisito)
        : [...prev.requisitos, requisito];
      return { ...prev, requisitos: nuevos };
    });
  };

  const { getError, getSuccess } = useNotification();

  const manejarGuardar = async () => {
    setMostrarConfirmacion(false);
    setGuardando(true);

    try {
      const vendedorNombre = (localStorage.getItem("current_username") || "").trim();
      
      const totalProducto = Number(datosFormulario.montoServicio) + Number(datosFormulario.montoSuplementario) + Number(datosFormulario.percepcion);

      const metadataTag = `[GRUPAL:${category || 'otros'}]`;
      const metadataGrupal = {
        categoria: category || 'otros',
        viajaAlExterior: datosFormulario.viajaAlExterior,
        cantidadColectivos: datosFormulario.cantidadColectivos,
        anioViaje: datosFormulario.anioViaje,
        montoServicio: datosFormulario.montoServicio,
        montoSuplementario: datosFormulario.montoSuplementario,
        percepcion: datosFormulario.percepcion,
        cantidadPasajeros: datosFormulario.cantidadPasajeros,
        requisitos: datosFormulario.requisitos,
        ...(category === 'eventos' ? {
          nombreEvento: datosFormulario.nombreEvento,
          anioEvento: datosFormulario.anioEvento,
          tipoEvento: datosFormulario.tipoEvento,
          cobroAsistentes: datosFormulario.cobroAsistentes
        } : {})
      };
      
      const metadataObs = {
        descripcion: `METADATA_GRUPAL: ${JSON.stringify(metadataGrupal)}`,
        fecha: new Date().toISOString().split('T')[0],
        usuario: "SISTEMA"
      };

      const finalDescripcion = metadataTag;

      const datosAPI: any = {
        concepto: datosFormulario.concepto,
        categoria: category,
        fecha_salida: datosFormulario.fechaSalida ? datosFormulario.fechaSalida.toISOString() : "",
        moneda: datosFormulario.monedaPrincipal,
        importe: totalProducto,
        descripcion: finalDescripcion,
        vendedor_nombre: vendedorNombre || "SISTEMA",
        observaciones: JSON.stringify([metadataObs]),
        adultos: datosFormulario.cantidadPasajeros,
        ninos: 0,
        infantes: 0,
        is_grupal: true,
        cliente: {
          nombre: datosFormulario.cliente,
          telefono: datosFormulario.telefonoCliente,
          email: datosFormulario.emailCliente,
        },
        aereosPaquetes: [],
        hotelesPaquetes: [],
        serviciosPaquetes: []
      };

      await product.create(datosAPI);
      getSuccess("Presupuesto grupal creado exitosamente");
      if (onPackageCreated) onPackageCreated();
    } catch (error: any) {
      getError(error?.response?.data?.message || "Error al crear el presupuesto");
    } finally {
      setGuardando(false);
    }
  };

  const isStep1Valid = category === 'eventos' 
    ? (datosFormulario.nombreEvento && datosFormulario.anioEvento)
    : (datosFormulario.concepto && datosFormulario.destino && datosFormulario.fechaSalida);

  const isStep2Valid = category === 'eventos'
    ? (datosFormulario.montoServicio > 0)
    : (datosFormulario.montoServicio > 0);

  const getConceptoPlaceholder = () => {
    switch (category) {
      case 'quince': return "Ej: Quince de [Nombre de la Quinceañera] - [Año]";
      case 'egresados': return "Ej: Egresados [Colegio] [División] - [Año]";
      case 'educativos': return "Ej: Viaje Educativo [Colegio] [Grado/Año] - [Concepto]";
      default: return "Ej: Nombre del grupo / Evento especial";
    }
  };



  if (category === 'eventos') {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Paper elevation={0} sx={{ p: 0, borderRadius: 4, bgcolor: 'background.paper', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8f9fa' }}>
            <Tabs value={pestañaActiva} onChange={(_, v) => setPestañaActiva(v)} textColor="primary" indicatorColor="primary" variant="fullWidth">
              <Tab label="1. Evento" />
              <Tab label="2. Montos del producto" disabled={!isStep1Valid} />
            </Tabs>
          </Box>

          <TabPanel value={pestañaActiva} index={0}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <TextField 
                  label="Nombre del Evento" 
                  fullWidth 
                  value={datosFormulario.nombreEvento} 
                  onChange={(e) => {
                    manejarCambioInput('nombreEvento', e.target.value);
                    manejarCambioInput('concepto', e.target.value);
                  }} 
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker 
                  label="Año" 
                  views={['year']}
                  openTo="year"
                  value={datosFormulario.anioEvento ? dayjs().year(parseInt(datosFormulario.anioEvento)) : null} 
                  onChange={(v) => {
                    const yearStr = v ? v.year().toString() : "";
                    manejarCambioInput('anioEvento', yearStr);
                    if (v) manejarCambioInput('fechaSalida', v.startOf('year'));
                  }} 
                  slotProps={{ textField: { fullWidth: true, variant: 'outlined', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }} 
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Evento</InputLabel>
                  <Select 
                    value={datosFormulario.tipoEvento} 
                    onChange={(e) => manejarCambioInput('tipoEvento', e.target.value)} 
                    label="Tipo de Evento"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="casamiento">Casamiento</MenuItem>
                    <MenuItem value="quince">Quince</MenuItem>
                    <MenuItem value="egresados">Egresados</MenuItem>
                    <MenuItem value="otros">Otros</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="contained" size="large" onClick={() => setPestañaActiva(1)} disabled={!isStep1Valid} sx={{ px: 4, py: 1.5, borderRadius: 2 }}>Siguiente</Button>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={pestañaActiva} index={1}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <TextField 
                  label="Monto del servicio/opción" 
                  type="number" 
                  fullWidth 
                  value={datosFormulario.montoServicio} 
                  onChange={(e) => manejarCambioInput('montoServicio', parseFloat(e.target.value) || 0)} 
                  InputProps={{ 
                    startAdornment: <InputAdornment position="start">{datosFormulario.monedaPrincipal === 'USD' ? 'U$D' : '$'}</InputAdornment>,
                    sx: { borderRadius: 2 } 
                  }} 
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Tipo de Moneda</InputLabel>
                  <Select value={datosFormulario.monedaPrincipal} onChange={(e) => manejarCambioInput('monedaPrincipal', e.target.value)} label="Tipo de Moneda" sx={{ borderRadius: 2 }}>
                    <MenuItem value="ARS">Pesos Argentinos (ARS)</MenuItem>
                    <MenuItem value="USD">Dólares Estadounidenses (USD)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={<Switch checked={datosFormulario.cobroAsistentes} onChange={(e) => manejarCambioInput('cobroAsistentes', e.target.checked)} color="primary" />}
                  label="¿Cobro de opción a asistentes?"
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button variant="outlined" size="large" onClick={() => setPestañaActiva(0)} sx={{ borderRadius: 2 }}>Anterior</Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large" 
                  onClick={() => setMostrarConfirmacion(true)} 
                  disabled={guardando || !isStep2Valid}
                  sx={{ borderRadius: 2 }}
                >
                  Crear Evento
                </Button>
              </Grid>
            </Grid>
          </TabPanel>

          <Dialog open={mostrarConfirmacion} onClose={() => setMostrarConfirmacion(false)}>
            <DialogTitle>Confirmar</DialogTitle>
            <DialogContent><DialogContentText>¿Desea crear este evento?</DialogContentText></DialogContent>
            <DialogActions><Button onClick={() => setMostrarConfirmacion(false)}>Cancelar</Button><Button onClick={manejarGuardar} variant="contained">Confirmar</Button></DialogActions>
          </Dialog>
        </Paper>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper elevation={0} sx={{ p: 0, borderRadius: 4, bgcolor: 'background.paper', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8f9fa' }}>
          <Tabs value={pestañaActiva} onChange={(_, v) => setPestañaActiva(v)} textColor="primary" indicatorColor="primary" variant="fullWidth">
            <Tab label="1. Destino" />
            <Tab label="2. Montos del producto" disabled={!isStep1Valid} />
            <Tab label="3. Cliente y datos de pasajeros" disabled={!isStep1Valid || !isStep2Valid} />
          </Tabs>
        </Box>

        <TabPanel value={pestañaActiva} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <TextField label="Nombre del Grupo / Concepto" fullWidth value={datosFormulario.concepto} onChange={(e) => manejarCambioInput('concepto', e.target.value)} placeholder={getConceptoPlaceholder()} InputProps={{ sx: { borderRadius: 2, fontSize: '1.1rem', fontWeight: 500 } }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker 
                label="Año de salida" 
                views={['year']}
                openTo="year"
                value={datosFormulario.fechaSalida} 
                onChange={(v) => manejarCambioInput('fechaSalida', v)} 
                slotProps={{ textField: { fullWidth: true, variant: 'outlined', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }} 
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Autocomplete options={OPCIONES_DESTINO} value={datosFormulario.destino} onChange={(_, v) => manejarCambioInput('destino', v)} renderInput={(params) => <TextField {...params} label="Opciones de Destino" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Cantidad de Colectivos" type="number" fullWidth value={datosFormulario.cantidadColectivos} onChange={(e) => manejarCambioInput('cantidadColectivos', parseInt(e.target.value) || 0)} InputProps={{ sx: { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel control={<Switch checked={datosFormulario.viajaAlExterior} onChange={(e) => manejarCambioInput('viajaAlExterior', e.target.checked)} color="primary" />} label="¿Viaje al exterior?" />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" size="large" onClick={() => setPestañaActiva(1)} disabled={!isStep1Valid} sx={{ px: 4, py: 1.5, borderRadius: 2 }}>Siguiente</Button>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={pestañaActiva} index={1}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Tipo de Moneda</InputLabel>
                <Select value={datosFormulario.monedaPrincipal} onChange={(e) => manejarCambioInput('monedaPrincipal', e.target.value)} label="Tipo de Moneda" sx={{ borderRadius: 2 }}>
                  <MenuItem value="ARS">Pesos Argentinos (ARS)</MenuItem>
                  <MenuItem value="USD">Dólares Estadounidenses (USD)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                label="Monto del servicio/opción" 
                type="number" 
                fullWidth 
                value={datosFormulario.montoServicio} 
                onChange={(e) => manejarCambioInput('montoServicio', parseFloat(e.target.value) || 0)} 
                InputProps={{ 
                  startAdornment: <InputAdornment position="start">{datosFormulario.monedaPrincipal === 'USD' ? 'U$D' : '$'}</InputAdornment>,
                  sx: { borderRadius: 2 } 
                }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                label="Monto suplementario del producto" 
                type="number" 
                fullWidth 
                value={datosFormulario.montoSuplementario} 
                onChange={(e) => manejarCambioInput('montoSuplementario', parseFloat(e.target.value) || 0)} 
                InputProps={{ 
                  startAdornment: <InputAdornment position="start">{datosFormulario.monedaPrincipal === 'USD' ? 'U$D' : '$'}</InputAdornment>,
                  sx: { borderRadius: 2 } 
                }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                label="Percepción" 
                type="number" 
                fullWidth 
                value={datosFormulario.percepcion} 
                onChange={(e) => manejarCambioInput('percepcion', parseFloat(e.target.value) || 0)} 
                InputProps={{ 
                  startAdornment: <InputAdornment position="start">{datosFormulario.monedaPrincipal === 'USD' ? 'U$D' : '$'}</InputAdornment>,
                  sx: { borderRadius: 2 } 
                }} 
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed', borderColor: 'divider', textAlign: 'right' }}>
                <Typography variant="h6" color="text.secondary">Total Producto</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  {datosFormulario.monedaPrincipal === 'USD' ? 'U$D' : '$'} {(Number(datosFormulario.montoServicio) + Number(datosFormulario.montoSuplementario) + Number(datosFormulario.percepcion)).toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button variant="outlined" size="large" onClick={() => setPestañaActiva(0)} sx={{ borderRadius: 2 }}>Anterior</Button>
            <Button variant="contained" size="large" onClick={() => setPestañaActiva(2)} disabled={!isStep2Valid} sx={{ borderRadius: 2 }}>Siguiente</Button>
          </Box>
        </TabPanel>

        <TabPanel value={pestañaActiva} index={2}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Datos del Cliente Responsable</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}><TextField label="Nombre Responsable" fullWidth value={datosFormulario.cliente} onChange={(e) => manejarCambioInput('cliente', e.target.value)} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Teléfono" fullWidth value={datosFormulario.telefonoCliente} onChange={(e) => manejarCambioInput('telefonoCliente', e.target.value)} /></Grid>
                <Grid item xs={12} md={3}><TextField label="Email" fullWidth value={datosFormulario.emailCliente} onChange={(e) => manejarCambioInput('emailCliente', e.target.value)} /></Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>Pasajeros</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField 
                    label="Cantidad de pasajeros" 
                    type="number" 
                    fullWidth 
                    value={datosFormulario.cantidadPasajeros} 
                    onChange={(e) => manejarCambioInput('cantidadPasajeros', parseInt(e.target.value) || 0)} 
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>Seleccione los datos que requiere el producto</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {REQUISITOS_OPCIONES.map((req) => (
                  <Button
                    key={req}
                    variant={datosFormulario.requisitos.includes(req) ? "contained" : "outlined"}
                    onClick={() => toggleRequisito(req)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.85rem',
                      py: 0.5,
                      px: 2,
                      minWidth: 'fit-content',
                      backgroundColor: datosFormulario.requisitos.includes(req) ? 'primary.main' : 'transparent',
                      color: datosFormulario.requisitos.includes(req) ? 'white' : 'text.primary',
                      borderColor: 'divider',
                      '&:hover': {
                        backgroundColor: datosFormulario.requisitos.includes(req) ? 'primary.dark' : '#f5f5f5',
                      }
                    }}
                  >
                    {req}
                  </Button>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button variant="outlined" size="large" onClick={() => setPestañaActiva(1)}>Anterior</Button>
              <Button variant="contained" color="primary" size="large" onClick={() => setMostrarConfirmacion(true)} disabled={guardando || !datosFormulario.cliente}>Crear Presupuesto Grupal</Button>
            </Grid>
          </Grid>
        </TabPanel>

        <Dialog open={mostrarConfirmacion} onClose={() => setMostrarConfirmacion(false)}>
          <DialogTitle>Confirmar</DialogTitle>
          <DialogContent><DialogContentText>¿Desea crear este presupuesto?</DialogContentText></DialogContent>
          <DialogActions><Button onClick={() => setMostrarConfirmacion(false)}>Cancelar</Button><Button onClick={manejarGuardar} variant="contained">Confirmar</Button></DialogActions>
        </Dialog>
      </Paper>
    </LocalizationProvider>
  );
};

export default NuevoPresupuestoGrupal;
